import React, { useState } from 'react';
import styled from 'styled-components';

const OfferingContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #2c5aa0 0%, #1e3a8a 100%);
  color: #fff;
  padding: 80px 0;
  text-align: center;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ContentSection = styled.section`
  padding: 80px 0;
  background: #f8f9fa;

  @media (max-width: 768px) {
    padding: 50px 0;
  }
`;

const SectionInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  font-size: 30px;
  color: #2c5aa0;
  text-align: center;
  margin-bottom: 50px;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 35px;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 40px 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const CardIcon = styled.div`
  font-size: 52px;
  margin-bottom: 20px;
`;

const CardTitle = styled.h3`
  font-size: 22px;
  color: #333;
  margin-bottom: 20px;
`;

const InfoRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 15px;
  gap: 12px;

  &:last-of-type {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: #888;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
`;

const InfoValue = styled.span`
  color: #333;
  font-weight: 600;
  word-break: break-all;
  text-align: right;
`;

const VenmoQR = styled.img`
  width: 180px;
  height: 180px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
`;

const VenmoHandle = styled.a`
  display: inline-block;
  font-size: 17px;
  font-weight: 700;
  color: #3d95ce;
  text-decoration: none;
  margin-top: 8px;

  &:hover {
    text-decoration: underline;
  }
`;

const CopyButton = styled.button`
  margin-top: 14px;
  background: #2c5aa0;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 20px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #1e3a8a;
  }
`;

const CopiedMsg = styled.span`
  display: block;
  margin-top: 8px;
  font-size: 13px;
  color: #28a745;
`;

const WalletAddress = styled.div`
  font-size: 13px;
  color: #555;
  word-break: break-all;
  background: #f4f4f4;
  border-radius: 6px;
  padding: 10px 14px;
  margin-top: 14px;
  width: 100%;
  text-align: left;
  font-family: monospace;
`;

const NetworkBadge = styled.span`
  display: inline-block;
  font-size: 12px;
  background: #e8f0fe;
  color: #2c5aa0;
  border-radius: 20px;
  padding: 4px 12px;
  font-weight: 600;
  margin-bottom: 14px;
`;

const Note = styled.p`
  font-size: 14px;
  color: #888;
  margin-top: 16px;
  line-height: 1.6;
`;

const USDC_ADDRESS = '0xd38Aa0f7465FeCCCC32189027A5F4d48E6106670';

const Offering: React.FC = () => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copy = (text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  return (
    <OfferingContainer>
      <HeroSection>
        <HeroContent>
          <Title>헌금 안내</Title>
          <Subtitle>하나님 나라를 위한 귀한 헌신에 감사드립니다</Subtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <SectionInner>
          <SectionTitle>헌금 방법</SectionTitle>
          <CardsGrid>

            {/* Zelle */}
            <Card>
              <CardIcon>💜</CardIcon>
              <CardTitle>Zelle</CardTitle>
              <VenmoQR src="/zelle-qr.jpeg" alt="Zelle QR Code" />
              <VenmoHandle
                href={`mailto:ijbcorg@gmail.com`}
                style={{ color: '#6d1ed4' }}
              >
                ijbcorg@gmail.com
              </VenmoHandle>
              <Note>QR 코드를 스캔하거나 위 이메일로 Zelle 송금해 주세요.</Note>
            </Card>

            {/* Venmo */}
            <Card>
              <CardIcon>💙</CardIcon>
              <CardTitle>Venmo</CardTitle>
              <VenmoQR src="/venmo-qr.png" alt="Venmo QR Code" />
              <VenmoHandle
                href="https://venmo.com/u/ijbc-irvinejoongangbaptistchur"
                target="_blank"
                rel="noopener noreferrer"
              >
                @ijbc-irvinejoongangbaptistchur
              </VenmoHandle>
              <Note>QR 코드를 스캔하거나 위 계정으로 송금해 주세요.</Note>
            </Card>

            {/* Hanmi Bank */}
            <Card>
              <CardIcon>🏦</CardIcon>
              <CardTitle>Hanmi Bank 계좌이체</CardTitle>
              <InfoRow>
                <InfoLabel>은행</InfoLabel>
                <InfoValue>Hanmi Bank</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Routing #</InfoLabel>
                <InfoValue>122039399</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Account #</InfoLabel>
                <InfoValue>550404131</InfoValue>
              </InfoRow>
              <CopyButton onClick={() => copy('122039399', 'routing')}>
                Routing # 복사
              </CopyButton>
              {copiedField === 'routing' && <CopiedMsg>복사되었습니다!</CopiedMsg>}
              <CopyButton onClick={() => copy('550404131', 'account')} style={{ marginTop: '8px' }}>
                Account # 복사
              </CopyButton>
              {copiedField === 'account' && <CopiedMsg>복사되었습니다!</CopiedMsg>}
              <Note>온라인뱅킹 또는 은행 앱에서 계좌이체 시 사용해 주세요.</Note>
            </Card>

            {/* USDC / Circle */}
            <Card>
              <CardIcon>💲</CardIcon>
              <CardTitle>USDC (스테이블코인)</CardTitle>
              <NetworkBadge>Ethereum / EVM 호환 네트워크</NetworkBadge>
              <InfoRow>
                <InfoLabel>토큰</InfoLabel>
                <InfoValue>USDC (Circle)</InfoValue>
              </InfoRow>
              <WalletAddress>{USDC_ADDRESS}</WalletAddress>
              <CopyButton onClick={() => copy(USDC_ADDRESS, 'usdc')}>
                지갑 주소 복사
              </CopyButton>
              {copiedField === 'usdc' && <CopiedMsg>복사되었습니다!</CopiedMsg>}
              <Note>
                Ethereum, Base, Polygon 등 EVM 호환 네트워크에서 USDC를 전송해 주세요.
                반드시 USDC만 전송해 주시고, 네트워크를 확인 후 전송해 주세요.
              </Note>
            </Card>

          </CardsGrid>
        </SectionInner>
      </ContentSection>
    </OfferingContainer>
  );
};

export default Offering;
